import type * as NEA from '@spendesk/general-type-helpers/NonEmptyArray';

import {
  useMutation,
  type MutationState,
} from 'src/core/api/hooks/useMutation';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useNotifications } from 'src/core/modules/app/notifications';

type Payload =
  | {
      supplierIds: NEA.NonEmptyArray<string>;
    }
  | {
      accountPayableIds: NEA.NonEmptyArray<string>;
    };

export const useBulkUnarchiveSupplierMutation = (): MutationState<Payload> => {
  const { t } = useTranslation('global');
  const { successNotif, dangerNotif } = useNotifications();

  return useMutation<Payload>({
    request: {
      type: 'rest',
      method: 'post',
      target: 'companyAPI',
      endpoint: '/suppliers/bulk-unarchive',
    },
    options: {
      onError() {
        dangerNotif(
          t(
            'bookkeep.accountsPayable.panel.supplierUnarchivedErrorNotification',
          ),
        );
      },
      onSuccess({ client, payload }): void {
        successNotif(
          'supplierIds' in payload
            ? t(
                'bookkeep.accountsPayable.panel.supplierUnarchivedNotification',
                {
                  count: payload.supplierIds.length,
                },
              )
            : t(
                'bookkeep.accountsPayable.panel.accountPayableSupplierUnarchivedNotification',
              ),
        );

        client.invalidateQueries(['useAccountPayableSuppliersListQuery']);
        client.invalidateQueries([
          'useGetSuppliersWithoutAccountPayableListQuery',
        ]);

        if ('supplierIds' in payload) {
          for (const supplierId of payload.supplierIds) {
            client.invalidateQueries([
              'suppliers',
              'suppliersDetails',
              supplierId,
            ]);
          }
        }
      },
    },
  });
};
